/**
 * SOLID Review
 * S — Strong. One concern: the legal re-acceptance a signed-in player owes — whether the server's consent status calls
 *     for it, which documents it shows, and the consents accepting them records.
 * O — Good. Gating on a third document kind is one more literal in `REVIEWED`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. A factory, the gate's one question, and the two answers the dialog needs.
 * D — Strong. The legal schemas' types only.
 */
import type { IPayloadLegalDocument, IPayloadRecordConsent, ICommandPlanLegalReview } from '../ports/schemas/LegalMoves';

/** The two documents a player re-accepts, in the order they are shown and recorded. */
const REVIEWED: ReadonlyArray<string> = ['TERMS_OF_SERVICE', 'PRIVACY_POLICY'];

/**
 * THE RE-ACCEPTANCE GATE'S RULES, AS A VALUE OBJECT (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide
 * functions of FE4a (`decisions/legal/legalReview.ts`, 2026-10-04), which moved them out of ConsentReacceptanceGate.
 * The gate blocked a signed-in player whose recorded consent no longer matched the current Terms or Privacy Policy,
 * until they accepted again. Moved, not rewritten: it engages ONLY on an explicit "not all current" — any other answer
 * fails open — and only when there is a Terms or Privacy document to show; accepting records Terms, then Privacy, at
 * the versions shown. Nothing here is the session's: the gate's own dialog is its business.
 */
export class LegalReview {
  private constructor(private readonly documents: ReadonlyArray<IPayloadLegalDocument>) {}

  /** The review out of the documents the server publishes: Terms, then Privacy, whichever exist. */
  static of(published: ReadonlyArray<IPayloadLegalDocument>): LegalReview {
    const shown: IPayloadLegalDocument[] = [];
    for (const kind of REVIEWED) {
      const document = published.find((d) => d.type === kind);
      if (document !== undefined) shown.push(document);
    }
    return new LegalReview(shown);
  }

  /** The consent status calls for the documents only when the server explicitly says a consent is stale. */
  static isDue(status: ICommandPlanLegalReview['status']): boolean {
    return status.allCurrent === false;
  }

  /** The documents to re-accept — none, and the gate stays closed. */
  shown(): IPayloadLegalDocument[] {
    return [...this.documents];
  }

  /** Accepting: one consent per document shown, at the version shown, Terms first. */
  consents(): IPayloadRecordConsent[] {
    return this.documents.map((document): IPayloadRecordConsent => ({ documentType: document.type, documentVersion: document.version }));
  }
}
